import { Visibility } from "./subscription";
import { ProductRiskMetrics } from "./productRiskMetrics";

export type { Visibility };

// --- Shared Enums ---

export type ContractType = "STOCK" | "OPTION";
export type Position = "LONG" | "SHORT";

// --- Generic V3 API response envelope ---

export interface V3ApiResponse<T> {
  message: string;
  data: T;
}

// --- Pagination ---

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface PaginatedResponse<T> {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: T[];
}

export function buildPaginationQuery(params?: PaginationParams): string {
  if (!params) return "";
  const parts: string[] = [];
  if (params.page !== undefined) parts.push(`page=${params.page}`);
  if (params.pageSize !== undefined) parts.push(`pageSize=${params.pageSize}`);
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

// --- Proposed Types (pending backend endpoints Q18, Q19) ---

export interface PortfolioRef {
  portfolioId: number;
  title: string;
  versionId: number;
}

export interface PortfolioBasketState {
  basketId: number;
  activeVersionId: number | null;
  pendingVersionId: number | null;
}

export interface PortfolioUsageItem {
  portfolioId: number;
  title: string;
  versionId: number;
  version: string;
  publishedAtUtc: string;
  basket: {
    weight: number;
    basketVersionId: number;
  };
}

// --- Basket Request Payloads ---

export interface StockContractInput {
  conId: number;
  weight: number;
}

export interface CreateBasketRequest {
  title: string;
  description?: string;
  visibility: Visibility;
  contracts: {
    stocks: StockContractInput[];
  };
}

export interface UpdateBasketRequest {
  title?: string;
  description?: string;
  visibility?: Visibility;
  contracts: {
    stocks: StockContractInput[];
  };
}

// --- Basket PATCH / Composition Request Types ---

export interface PatchBasketRequest {
  title?: string;
  description?: string;
  visibility?: Visibility;
  metadata?: Record<string, unknown> | null;
  // PWA-2368: admin-only rate-card binding. A positive id binds; explicit `null`
  // clears (falls back to provider/default); omit the key to leave it unchanged.
  rateCardId?: number | null;
}

export interface BasketCompositionRequest {
  stocks: StockContractInput[];
}

// --- Basket Response Types ---

export interface BasketLatestPublishedVersion {
  id: number;
  version: string;
  publishedAtUtc: string;
  contractCount: number;
}

export interface BasketListItem {
  id: number;
  title: string;
  description: string | null;
  visibility: string;
  createdAtUtc: string;
  draft: {
    contracts: { stocks: number };
  };
  active: {
    contracts: { stocks: number };
  };
  latestPublishedVersion: BasketLatestPublishedVersion | null;
}

export interface BasketDetail {
  basket: {
    id: number;
    title: string;
    description: string | null;
    visibility: string;
    metadata?: Record<string, unknown> | null;
    createdAtUtc: string;
  };
  contracts: {
    stocks: StockContractInput[];
  };
  latestPublishedVersion: BasketLatestPublishedVersion | null;
}

export interface PublishBasketResult {
  version: string;
  publishedAtUtc: string;
  contracts: { stocks: number };
}

export interface BasketContractState {
  conId: number;
  activeVersionId: number | null;
  pendingVersionId: number | null;
  activatedAtUtc: string | null;
  contractTimezone: string;
}

// --- Portfolio Request Payloads ---

export interface ContractInput {
  conId: number;
  weight: number;
}

export interface BasketInput {
  basketId: number;
  weight: number;
}

export interface BasketReference {
  basketId: number;
  basketVersionId: number;
  weight: number;
}

export interface ContractsInput {
  stocks: ContractInput[];
  options: ContractInput[];
}

export interface CreatePortfolioRequest {
  title: string;
  description?: string;
  visibility: Visibility;
  contracts: ContractsInput;
  baskets: BasketInput[];
}

export interface UpdatePortfolioRequest {
  title: string;
  description?: string;
  visibility: Visibility;
  contracts: ContractsInput;
  baskets: BasketInput[];
}

// --- Portfolio PATCH / Composition Request Types ---

export interface PatchPortfolioRequest {
  title?: string;
  description?: string;
  visibility?: Visibility;
  metadata?: Record<string, unknown> | null;
  // PWA-2368: admin-only rate-card binding. A positive id binds; explicit `null`
  // clears (falls back to provider/default); omit the key to leave it unchanged.
  rateCardId?: number | null;
}

export interface PortfolioCompositionRequest {
  contracts: ContractsInput;
  baskets: BasketInput[];
}

// --- Portfolio Response Types ---

export interface PortfolioLatestPublishedVersion {
  id: number;
  version: string;
  publishedAtUtc: string;
  stockCount: number;
  optionCount: number;
  basketCount: number;
}

export interface PortfolioListItem {
  id: number;
  title: string;
  description: string | null;
  visibility: string;
  createdAtUtc: string;
  draft: {
    contracts: { stocks: number; options: number };
    baskets: number;
  };
  active: {
    contracts: { stocks: number; options: number };
    baskets: number;
  };
  latestPublishedVersion: PortfolioLatestPublishedVersion | null;
}

export interface PortfolioDetail {
  portfolio: {
    id: number;
    title: string;
    description: string | null;
    visibility: string;
    metadata?: Record<string, unknown> | null;
    createdAtUtc: string;
  };
  contracts: {
    stocks: ContractInput[];
    options: ContractInput[];
  };
  baskets: BasketReference[];
  latestPublishedVersion: PortfolioLatestPublishedVersion | null;
}

export interface PublishPortfolioResult {
  version: string;
  publishedAtUtc: string;
  contracts: { stocks: number; options: number };
  baskets: number;
}

export interface PortfolioContractState {
  conId: number;
  contractType: ContractType;
  activeVersionId: number | null;
  pendingVersionId: number | null;
  activatedAtUtc: string | null;
  contractTimezone: string;
}

// --- Snapshot Types ---

export interface BasketSnapshotEntry {
  conId: number;
  trade: {
    action: "buy" | "sell" | "hold";
    amount?: number;
    weight?: number;
  };
}

export interface SnapshotTradeEntry {
  conId: number;
  action: "buy" | "sell" | "hold";
  newWeight: number;
  prevWeight: number;
}

export interface SnapshotCompositionEntry {
  conId: number;
  weight: number;
}

export interface BasketSnapshotItem {
  id: number;
  snapshot: {
    contracts: {
      stocks: {
        conId: number;
        weight: number;
        versionId: number;
        activatedAtUtc: string;
      }[];
    };
  };
  datetimeUtc: string;
}

export interface BasketSnapshot {
  snapshot: BasketSnapshotEntry[];
  datetimeUtc: string;
}

// --- Version and Trade Types ---

export interface VersionListItem {
  id: number;
  version: string;
  status: string;
  publishedAtUtc: string;
  publishedBy: string | null;
  createdAtUtc: string;
  contracts: { stocks: number; options?: number };
  baskets?: number;
}

// --- Version Metadata (comments) Types (PWA-1666) ---

// Frontend-owned, namespaced blob stored in the version metadata JSONB column.
// No author fields by design; the backend audit log retains the acting userId.
export interface VersionCommentMeta {
  comment: string;
  editedAtUtc?: string;
}

export interface VersionMetadataItem {
  id: number;
  versionId: number;
  version: string;
  metadata: VersionCommentMeta;
  createdAtUtc: string;
}

export interface CreateVersionMetadataRequest {
  metadata: VersionCommentMeta;
}

// --- Audit Log Types ---

export interface AuditLogEntry {
  id: number;
  action: string;
  trigger?: string;
  payload: unknown;
  createdBy: string;
  createdAtUtc: string;
}

// --- Boundary Normalization (PWA-1500 Section 5.5) ---

// --- Active Composition Types (PWA-1500) ---

export interface ActiveContractCompositionEntry {
  conId: number;
  versionId: number;
  weight: number;
  activatedAtUtc: string;
}

export interface ActiveBasketCompositionEntry {
  basketId: number;
  basketVersionId: number;
  versionId: number;
  weight: number;
  activatedAtUtc: string;
}

export interface PortfolioActiveComposition {
  contracts: {
    stocks: ActiveContractCompositionEntry[];
    options: ActiveContractCompositionEntry[];
  };
  baskets: ActiveBasketCompositionEntry[];
}

export interface BasketActiveComposition {
  contracts: {
    stocks: ActiveContractCompositionEntry[];
  };
}

export interface PortfolioSearchSnapshot {
  activated: PortfolioActiveComposition;
  performanceDrifts: PortfolioActiveComposition;
  partial?: boolean;
}

// --- After Activation Types ---

export interface BasketAfterActivationResult {
  composition: BasketActiveComposition;
  pendingContracts: { conId: number; pendingVersionId: number }[];
}

export interface PortfolioAfterActivationResult {
  composition: PortfolioActiveComposition;
  pendingContracts: {
    conId: number;
    contractType: "STOCK" | "OPTION";
    pendingVersionId: number;
  }[];
  pendingBaskets: { basketId: number; pendingVersionId: number }[];
}

// --- Composition Changes Types ---

export interface BasketCompositionChangeEntry {
  datetimeUtc: string;
  type: "snapshot" | "product_factor";
  composition: BasketActiveComposition;
}

export interface PortfolioCompositionChangeEntry {
  datetimeUtc: string;
  type: "snapshot" | "product_factor";
  composition: PortfolioActiveComposition;
}

export interface PaginatedCompositionChanges<T> {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: T[];
}

export interface BasketSearchSnapshot {
  activated: BasketActiveComposition;
  performanceDrifts: BasketActiveComposition;
  partial?: boolean;
}

export function isNewSnapshotShape(
  snapshot: unknown
): snapshot is PortfolioSearchSnapshot | BasketSearchSnapshot {
  return (
    snapshot !== null &&
    typeof snapshot === "object" &&
    "activated" in (snapshot as object)
  );
}

// --- Search Types ---

export interface ProductExposure {
  gross: number;
  net: number;
  long: number;
  short: number;
  cash: number;
}

// Rolling change-activity summary on the search response (PWA-2212/2213).
// Counts = number of published-and-activated product versions in the trailing
// 7 / 30 / 90 day UTC windows. No-history products return the zero object
// (`lastModifiedAtUtc: null`, all counts 0). NOT subscription-gated.
// Keys are the literal numeric strings "7d" / "30d" / "90d" (backend contract).
export interface ChangeSummary {
  lastModifiedAtUtc: string | null;
  "7d": number;
  "30d": number;
  "90d": number;
}

export interface ProductPerformance {
  return1D: number | null;
  return2D: number | null;
  return3D: number | null;
  return4D: number | null;
  return5D: number | null;
  return1W: number | null;
  return2W: number | null;
  return1M: number | null;
  return3M: number | null;
  return6M: number | null;
  return1Y: number | null;
  returnYtd: number | null;
  inception: number | null;
  all: number | null;
  computedAtUtc: string;
}

export type ProductRestrictionFlag =
  | "etf-non-us"
  | "etf-us"
  | "ptp"
  | "has-shorts";

export interface ProductSearchItem {
  productType: "BASKET" | "PORTFOLIO";
  productId: number;
  shareToken?: string | null;
  title: string;
  description: string | null;
  visibility: Visibility;
  providerId: number;
  providerName: string;
  numOfStocks: number;
  numOfOptions: number;
  numOfBaskets: number;
  settings: object;
  snapshot: PortfolioSearchSnapshot | BasketSearchSnapshot | object;
  performance: ProductPerformance | null;
  exposure: ProductExposure | null;
  // Per-product risk metrics (PWA-2031). `null` until the daily job first computes them.
  riskMetrics?: ProductRiskMetrics | null;
  data?: {
    hasPendingChanges: boolean;
  };
  // Rolling change-activity summary (PWA-2242). Optional for resilience to older
  // backend deploys; absence is treated identically to no-history.
  changeSummary?: ChangeSummary | null;
  // Like data embedded in the catalog listing (PWA-2205). Optional so the UI
  // stays resilient if an older backend deploy omits them; defaulted at the
  // read boundary (normalizeProductSearchItem / useProductLikes).
  likeCount?: number;
  likedByCurrentUser?: boolean;
  // Distinct contract remarks aggregated across the product's holdings. Drives
  // per-customer trading eligibility (see utils/productTradability). Optional
  // for resilience to older backend deploys; absence is treated as "no remarks",
  // i.e. tradable.
  contractRemarks?: string[] | null;
  // Viewer-specific restriction reasons computed by the investor product
  // search endpoint (PWA-2508). Optional/null for rolling-deploy resilience;
  // both are treated identically to an empty array.
  restrictionFlags?: ProductRestrictionFlag[] | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface ManagedStrategySearchCompositionEntry {
  entityType: "BASKET" | "PORTFOLIO";
  productId: number;
  providerId: number;
  // Percentage points from product search: 80 means 80%, not 0.80.
  weight: number;
}

export interface ManagedStrategySearchSnapshot {
  activated: {
    composition: ManagedStrategySearchCompositionEntry[];
  };
  // Current child allocations, also expressed in percentage points. Consumers
  // must use this whole array when non-empty and otherwise fall back to the
  // whole activated composition; the two arrays are not merged child by child.
  performanceDrifts?: ManagedStrategySearchCompositionEntry[] | null;
  // Subscriber teaser snapshots are not complete enough for holdings math.
  partial?: boolean;
}

// Investor product-search response for one managed strategy. This stays
// separate from ProductSearchItem so existing basket/portfolio consumers do
// not silently start accepting MANAGED_STRATEGY rows.
export interface ManagedStrategySearchItem
  extends Omit<
    ProductSearchItem,
    "productType" | "providerId" | "snapshot" | "data"
  > {
  productType: "MANAGED_STRATEGY";
  providerId: number | null;
  numOfPortfolios: number;
  snapshot: ManagedStrategySearchSnapshot;
  data: { hasPendingChanges: boolean } | null;
  hasShorts?: boolean;
}

// Admin-only search response shape (GET /v3/products/search/admin, via
// productSearchService.searchAdminProductsAllTypes()). Extends ProductSearchItem
// with the fields only that admin-scoped, all-types call can return
// (MANAGED_STRATEGY as a productType, a nullable providerId for it, and its
// numOfPortfolios holdings count). Never import this in investor/advisor-facing
// code, and never use it with the existing searchAdminProducts()/ProductSearchItem
// pair -- those stay BASKET/PORTFOLIO with a non-null providerId, unchanged.
export interface AdminProductSearchItem
  extends Omit<ProductSearchItem, "productType" | "providerId"> {
  productType: "BASKET" | "PORTFOLIO" | "MANAGED_STRATEGY";
  providerId: number | null;
  numOfPortfolios: number;
}
